<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  folder: { type: Object, required: true },
  otherFolders: { type: Array, required: true },
});

const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

const selectedTargetId = ref(null);
const confirmName = ref('');

const step = ref(props.folder._hasFields ? 'select' : 'confirm');

const canProceed = computed(() => selectedTargetId.value !== null);
const canConfirm = computed(
  () => confirmName.value.trim() === props.folder.name.trim()
);

function goToConfirm() {
  if (!canProceed.value) return;
  step.value = 'confirm';
}

function onConfirm() {
  if (!canConfirm.value) return;
  emit('confirm', { targetFolderId: selectedTargetId.value });
}
</script>

<template>
  <div
    class="fixed inset-0 z-[10010] flex items-center justify-center bg-black/40"
    @click.self="emit('cancel')"
  >
    <div
      class="bg-n-background rounded-xl shadow-2xl w-full max-w-sm mx-4 p-5 flex flex-col gap-4"
    >
      <!-- Passo 1: selecionar pasta de destino -->
      <template v-if="step === 'select'">
        <div class="flex flex-col gap-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('KANBAN.FOLDERS.DELETE_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{ t('KANBAN.FOLDERS.DELETE_MOVE_DESC', { name: folder.name }) }}
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-10">
            {{ t('KANBAN.FOLDERS.DELETE_TARGET_LABEL') }}
          </label>
          <div
            class="flex flex-col divide-y divide-n-weak border border-n-weak rounded-lg overflow-hidden"
          >
            <button
              v-for="f in otherFolders"
              :key="f.id"
              type="button"
              class="flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-n-alpha-1"
              :class="selectedTargetId === f.id ? 'bg-n-alpha-2' : ''"
              @click="selectedTargetId = f.id"
            >
              <span
                class="w-6 h-6 flex items-center justify-center rounded bg-n-surface-2 text-n-slate-10 shrink-0"
              >
                <span class="size-3.5" :class="f.icon" />
              </span>
              <span class="text-sm text-n-slate-12 flex-1">{{ f.name }}</span>
              <span
                v-if="selectedTargetId === f.id"
                class="i-lucide-check size-4 text-n-brand shrink-0"
              />
            </button>
          </div>
          <p v-if="!otherFolders.length" class="text-xs text-n-ruby-11 mt-1">
            {{ t('KANBAN.FOLDERS.DELETE_NO_TARGET') }}
          </p>
        </div>

        <div class="flex justify-end gap-2 pt-1">
          <Button
            faded
            slate
            sm
            :label="t('KANBAN.FOLDERS.CANCEL')"
            type="button"
            @click="emit('cancel')"
          />
          <Button
            sm
            ruby
            :label="t('KANBAN.FOLDERS.DELETE_NEXT')"
            :disabled="!canProceed || !otherFolders.length"
            type="button"
            @click="goToConfirm"
          />
        </div>
      </template>

      <!-- Passo 2: confirmar digitando o nome -->
      <template v-else>
        <div class="flex flex-col gap-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('KANBAN.FOLDERS.DELETE_CONFIRM_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{ t('KANBAN.FOLDERS.DELETE_CONFIRM_DESC', { name: folder.name }) }}
          </p>
        </div>

        <input
          v-model="confirmName"
          type="text"
          class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-surface-2 text-n-slate-12 outline-none focus:border-n-ruby-6"
          :placeholder="folder.name"
          @keydown.enter.prevent="onConfirm"
        />

        <div class="flex justify-end gap-2 pt-1">
          <Button
            faded
            slate
            sm
            :label="t('KANBAN.FOLDERS.CANCEL')"
            type="button"
            @click="emit('cancel')"
          />
          <Button
            sm
            ruby
            :label="
              t('KANBAN.FOLDERS.DELETE_CONFIRM_BTN', { name: folder.name })
            "
            :disabled="!canConfirm"
            type="button"
            @click="onConfirm"
          />
        </div>
      </template>
    </div>
  </div>
</template>
